/**
 * LayerManagerWidget.js
 * ArcGIS Maps SDK for JavaScript 4.x
 *
 * A side-panel widget that accepts an array of layer configuration objects,
 * adds those layers to the map, and lets users:
 *   • toggle visibility via checkboxes
 *   • override symbols (color, size/width, marker/line style, opacity)
 *
 * Supports: FeatureLayer | MapImageLayer | TileLayer | ImageryLayer | WMSLayer
 *
 * ── Usage (ESM / Vite / @arcgis/core) ──────────────────────────────────────
 *
 *   import MapView   from '@arcgis/core/views/MapView.js';
 *   import Map       from '@arcgis/core/Map.js';
 *   import LayerManagerWidget from './LayerManagerWidget.js';
 *   import './LayerManagerWidget.css';
 *
 *   const view = new MapView({ container: 'viewDiv', map: new Map({ basemap: 'streets' }) });
 *
 *   const widget = new LayerManagerWidget({
 *     view,
 *     position : 'left',   // 'left' | 'right'
 *     title    : 'Layers',
 *     layerConfigs: [
 *       {
 *         id           : 'quakes',
 *         title        : 'Earthquakes',
 *         type         : 'feature',           // 'feature' | 'map-image' | 'tile'
 *         url          : 'https://services.arcgis.com/.../FeatureServer/0',
 *         geometryType : 'point',             // 'point' | 'polyline' | 'polygon'
 *         visible      : true,
 *         opacity      : 1,
 *         symbol: {                           // optional initial symbol override
 *           color       : '#e63946',
 *           outlineColor: '#ffffff',
 *           size        : 10,
 *           style       : 'circle',
 *         },
 *       },
 *       {
 *         id    : 'imagery',
 *         title : 'World Imagery',
 *         type  : 'tile',
 *         url   : 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer',
 *       },
 *     ],
 *   });
 *
 * ── LayerConfig shape ───────────────────────────────────────────────────────
 * @typedef {Object} LayerConfig
 * @property {string}  id            - Unique identifier
 * @property {string}  title         - Display name
 * @property {'feature'|'map-image'|'tile'|'imagery'|'wms'} type
 * @property {string}  url           - Service URL
 * @property {'point'|'polyline'|'polygon'} [geometryType]
 *   Required for FeatureLayer symbol overrides. If omitted the widget
 *   auto-detects it after the layer loads.
 * @property {boolean} [visible=true]
 * @property {number}  [opacity=1]   - 0–1
 * @property {Object}  [symbol]
 * @property {string}  [symbol.color='#0078d4']
 * @property {string}  [symbol.outlineColor='#ffffff']
 * @property {number}  [symbol.size=8]        - points (point layers)
 * @property {number}  [symbol.width=2]       - px (line/polygon outline)
 * @property {Object}  [popupTemplate]        - Esri PopupTemplate definition (auto-generated by builder)
 * @property {Array}   [sublayers]            - MapImageLayer / WMSLayer sublayer definitions
 * @property {string}  [token]                - Auth token appended to layer requests
 * @property {string}  [symbol.style]         - marker or line style key
 */

 

// ── Constants ─────────────────────────────────────────────────────────────────

const MARKER_STYLES = ['circle', 'square', 'diamond', 'cross', 'x', 'triangle'];
const LINE_STYLES   = ['solid', 'dash', 'dot', 'dash-dot', 'long-dash'];
const MARKER_STYLE_MAP = {
  esriSMSCircle: 'circle',
  esriSMSSquare: 'square',
  esriSMSDiamond: 'diamond',
  esriSMSCross: 'cross',
  esriSMSX: 'x',
  esriSMSTriangle: 'triangle',
  circle: 'circle',
  square: 'square',
  diamond: 'diamond',
  cross: 'cross',
  x: 'x',
  triangle: 'triangle',
};
const LINE_STYLE_MAP = {
  esriSLSSolid: 'solid',
  esriSLSDash: 'dash',
  esriSLSDot: 'dot',
  esriSLSDashDot: 'dash-dot',
  esriSLSDashDotDot: 'dash-dot',
  solid: 'solid',
  dash: 'dash',
  dot: 'dot',
  'dash-dot': 'dash-dot',
  'long-dash': 'long-dash',
};
const LABEL_PLACEMENT_MAP = {
  esriServerPolygonPlacementAlwaysHorizontal: 'always-horizontal',
  esriServerPointLabelPlacementAboveCenter: 'above-center',
  esriServerPointLabelPlacementBelowCenter: 'below-center',
  esriServerLinePlacementAboveAlong: 'above-along',
  esriServerLinePlacementCenterAlong: 'center-along',
  esriServerLinePlacementBelowAlong: 'below-along',
};
const VALID_LABEL_PLACEMENTS = new Set([
  'above-after','above-along','above-before','above-center','above-end','above-left','above-right','above-start',
  'always-horizontal',
  'below-after','below-along','below-before','below-center','below-end','below-left','below-right','below-start',
  'center-after','center-along','center-before','center-center','center-end','center-left','center-right','center-start',
]);

const DEFAULT_SYMBOL = {
  color       : '#0078d4',
  outlineColor: '#ffffff',
  size        : 8,
  width       : 2,
  style       : 'circle',
  opacity     : 1,
};

// ── Widget ────────────────────────────────────────────────────────────────────

export default class LayerManagerWidget {

  /**
   * @param {Object}        options
   * @param {__esri.MapView|__esri.SceneView} options.view
   * @param {LayerConfig[]} options.layerConfigs
   * @param {'left'|'right'} [options.position='left']
   * @param {string}        [options.title='Layers']
   */
  constructor ({
    view,
    layerConfigs = [],
    position = 'left',
    title = 'Layers',
    mode = 'view',
    canEnterEdit = true,
    onRequestDiscover = null,
    onRequestSave = null,
    onModeChange = null,
    onDirtyChange = null,
    esri = null,
    collapsed = false,
  } = {}) {
    if (!view) throw new Error('[LayerManagerWidget] options.view is required');

    this._view     = view;
    this._pos      = position;
    this._title    = title;
    this._configs  = layerConfigs;
    this._mode = mode === 'edit' ? 'edit' : 'view';
    this._canEnterEdit = !!canEnterEdit;
    this._onRequestDiscover = onRequestDiscover;
    this._onRequestSave = onRequestSave;
    this._onModeChange = onModeChange;
    this._onDirtyChange = onDirtyChange;
    this._dirty = false;
    this._busy = false;
    this._esri = esri || {};

    if (
      !this._esri.FeatureLayer ||
      !this._esri.MapImageLayer ||
      !this._esri.TileLayer ||
      !this._esri.ImageryLayer ||
      !this._esri.WMSLayer ||
      !this._esri.SimpleRenderer ||
      !this._esri.SimpleMarkerSymbol ||
      !this._esri.SimpleLineSymbol ||
      !this._esri.SimpleFillSymbol
    ) {
      throw new Error('[LayerManagerWidget] Missing esri dependencies. Use createCombinedControlForEsriLoader(...) or pass options.esri.');
    }

    /** @type {Map<string, { config: LayerConfig, layer: __esri.Layer, sym: Object }>} */
    this._states   = new Map();
    this._panel    = null;
    this._collapsed = !!collapsed;
    this._scaleWatchHandle = null;

    this._init();
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  _init () {
    this._createLayers();
    this._buildPanel();
    this._attachPanel();
    this._bindScaleWatcher();
  }

  _createLayers () {
    for (const cfg of this._configs) {
      const layer = this._makeLayer(cfg);
      if (!layer) continue;

      const sym = this._defaultSym(cfg);
      this._states.set(cfg.id, { config: cfg, layer, sym });

      // Apply initial renderer for FeatureLayer if geometryType is known
      if (cfg.type === 'feature') {
        layer.when(() => this._applySafeLabeling(layer, cfg)).catch(() => {});

        const canApplySimpleRenderer = cfg.isSymbolEditable !== false && (!cfg.rendererType || cfg.rendererType === 'simple');
        if (canApplySimpleRenderer && cfg.geometryType) {
          this._applyRenderer(layer, sym, cfg.geometryType);
        } else {
          // Auto-detect after load
          layer.when(() => {
            const gt = this._detectGeomType(layer);
            const state = this._states.get(cfg.id);
            if (state && gt) {
              state.config = { ...state.config, geometryType: gt };
              const canApplyDetected = state.config.isSymbolEditable !== false && (!state.config.rendererType || state.config.rendererType === 'simple');
              if (canApplyDetected) {
                this._applyRenderer(layer, state.sym, gt);
              }
              // Rebuild the item to show correct controls
              this._rebuildItem(cfg.id);
            }
          });
        }
      }

      this._view.map.add(layer);
    }
  }

  _makeLayer (cfg) {
    const { FeatureLayer, MapImageLayer, TileLayer, ImageryLayer, WMSLayer } = this._esri;
    if (this._shouldForceMapImage(cfg)) {
      const fallback = this._toMapImageFallbackConfig(cfg);
      if (fallback) {
        return new MapImageLayer({
          id: fallback.id,
          url: fallback.url,
          title: fallback.title,
          visible: fallback.visible,
          opacity: fallback.opacity,
          sublayers: fallback.sublayers,
          ...(fallback.token ? { customParameters: { token: fallback.token } } : {}),
        });
      }
    }

    const base = {
      id     : cfg.id,
      url    : cfg.url,
      title  : cfg.title || cfg.id,
      visible: cfg.visible !== false,
      opacity: cfg.opacity ?? 1,
    };
    switch (cfg.type) {
      case 'feature':
        return new FeatureLayer({
          ...base,
          // Pass-through builder-generated fields (ignored if undefined)
          popupTemplate: cfg.popupTemplate || undefined,
          displayField : cfg.displayField  || undefined,
          minScale     : cfg.minScale      ?? undefined,
          maxScale     : cfg.maxScale      ?? undefined,
          // Token for secured services
          ...(cfg.token ? { customParameters: { token: cfg.token } } : {}),
        });

      case 'map-image':
        {
          const sublayers = Array.isArray(cfg.sublayers)
            ? cfg.sublayers.map(s => ({
                id: s.id,
                title: s.title,
                visible: s.visible !== false,
              }))
            : undefined;
        return new MapImageLayer({
          ...base,
          sublayers,
          ...(cfg.token ? { customParameters: { token: cfg.token } } : {}),
        });
        }

      case 'tile':
        return new TileLayer({
          ...base,
          ...(cfg.token ? { customParameters: { token: cfg.token } } : {}),
        });

      case 'imagery':
        return new ImageryLayer({
          ...base,
          popupTemplate: cfg.popupTemplate || undefined,
          ...(cfg.token ? { customParameters: { token: cfg.token } } : {}),
        });

      case 'wms':
        return new WMSLayer({
          ...base,
          sublayers: cfg.sublayers || [{ name: '0' }],
        });

      default:
        console.warn(`[LayerManagerWidget] Unknown type "${cfg.type}" for "${cfg.id}"`);
        return null;
    }
  }

  _defaultSym (cfg) {
    const s = cfg.symbol || {};
    const g = cfg.geometryType || 'polygon';
    const normalizedStyle =
      g === 'polyline'
        ? this._normalizeLineStyle(s.style)
        : this._normalizeMarkerStyle(s.style);
    return {
      color       : s.color        ?? DEFAULT_SYMBOL.color,
      outlineColor: s.outlineColor ?? DEFAULT_SYMBOL.outlineColor,
      size        : s.size         ?? DEFAULT_SYMBOL.size,
      width       : s.width        ?? DEFAULT_SYMBOL.width,
      style       : normalizedStyle ?? (g === 'point' ? 'circle' : 'solid'),
      opacity     : cfg.opacity    ?? DEFAULT_SYMBOL.opacity,
    };
  }

  _detectGeomType (layer) {
    const t = layer.geometryType;
    if (!t) return null;
    if (t === 'point' || t === 'multipoint') return 'point';
    if (t.includes('line'))   return 'polyline';
    if (t.includes('polygon')) return 'polygon';
    return null;
  }

  // ── Renderer ──────────────────────────────────────────────────────────────

  _applyRenderer (layer, sym, geometryType) {
    const { SimpleRenderer, SimpleMarkerSymbol, SimpleLineSymbol, SimpleFillSymbol } = this._esri;
    let symbol;
    switch (geometryType) {
      case 'point':
        symbol = new SimpleMarkerSymbol({
          style  : this._normalizeMarkerStyle(sym.style) || 'circle',
          color  : sym.color,
          size   : `${sym.size}px`,
          outline: { color: sym.outlineColor, width: 1 },
        });
        break;
      case 'polyline':
        symbol = new SimpleLineSymbol({
          style: this._normalizeLineStyle(sym.style) || 'solid',
          color: sym.color,
          width: sym.width,
        });
        break;
      case 'polygon':
      default:
        symbol = new SimpleFillSymbol({
          style  : 'solid',
          color  : sym.color,
          outline: { color: sym.outlineColor, width: sym.width },
        });
        break;
    }
    layer.renderer = new SimpleRenderer({ symbol });
    layer.opacity  = sym.opacity;
  }

  // ── Panel HTML ────────────────────────────────────────────────────────────

  _buildPanel () {
    this._panel = document.createElement('div');
    this._panel.className = `lmw-panel lmw-panel--${this._pos}`;
    this._panel.innerHTML = this._renderPanel();
    if (this._collapsed) this._panel.classList.add('lmw-collapsed');
    this._bindEvents();
  }

  _renderPanel () {
    const items = [...this._states.values()].map(s => this._renderItem(s)).join('');
    const isEdit = this._mode === 'edit';
    return `
      <div class="lmw-header">
        <span class="lmw-header-title">${this._title}</span>
        ${this._canEnterEdit ? `
          <button class="lmw-btn-icon" title="${isEdit ? 'Switch to view mode' : 'Switch to edit mode'}" data-action="toggleEdit">
            ${isEdit ? '👁' : '✎'}
          </button>
        ` : ''}
        ${isEdit ? `
          <button class="lmw-btn-icon" title="Discover layers" data-action="discover">＋</button>
          <button class="lmw-btn-icon ${this._dirty ? 'active' : ''}" title="Save changes" data-action="saveChanges">💾</button>
        ` : ''}
        <button class="lmw-btn-icon lmw-toggle-all" title="Toggle all layers visibility" data-action="toggleAll">
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="8" cy="8" r="6"/>
            <circle cx="8" cy="8" r="2" fill="currentColor" stroke="none"/>
          </svg>
        </button>
        <button class="lmw-btn-icon lmw-collapse-btn" title="Collapse panel" data-action="collapse">
          <svg class="lmw-chevron" viewBox="0 0 16 16" width="14" height="14" fill="none"
               stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <polyline points="10,4 6,8 10,12"/>
          </svg>
        </button>
      </div>
      ${this._busy ? '<div class="lmw-busy-overlay"><span class="lmw-spinner"></span><span>Working…</span></div>' : ''}
      <div class="lmw-body">
        ${items || '<p class="lmw-empty">No layers configured.</p>'}
      </div>`;
  }

  _renderItem ({ config: cfg, layer, sym }) {
    const id        = cfg.id;
    const g         = cfg.geometryType || null;   // may be null if not yet known
    const isFeature = cfg.type === 'feature';
    const hasColor  = isFeature || cfg.type === 'map-image';
    const swatchBg  = this._toHex(sym.color);
    const canEditSymbol = cfg.isSymbolEditable !== false;

    const isEditMode = this._mode === 'edit';
    return `
      <div class="lmw-item" data-id="${id}">

        <!-- ── Layer row ── -->
        <div class="lmw-row">
          <label class="lmw-check" title="Toggle visibility">
            <input type="checkbox" class="lmw-check-input" data-id="${id}" data-action="visibility"
              ${layer.visible ? 'checked' : ''}>
            <span class="lmw-check-box"></span>
          </label>
          <span class="lmw-swatch" data-swatch="${id}" style="background:${swatchBg}"></span>
          <span class="lmw-name" title="${cfg.title || id}">${cfg.title || id}</span>
          <span class="lmw-badge lmw-badge--${cfg.type}">${{ 'feature':'F','map-image':'MI','tile':'T','imagery':'Img','wms':'W' }[cfg.type] ?? cfg.type.charAt(0).toUpperCase()}</span>
          ${isEditMode ? `
            <button class="lmw-btn-icon lmw-sym-toggle" data-id="${id}" data-action="toggleSym"
              title="${canEditSymbol ? 'Edit symbology' : 'Symbol editing disabled for non-simple renderer'}"
              ${canEditSymbol ? '' : 'disabled'}>&#9881;</button>
            <button class="lmw-btn-icon" data-id="${id}" data-action="deleteLayer"
              title="Delete layer">🗑</button>
          ` : ''}
        </div>

        <!-- ── Symbol panel ── -->
        <div class="lmw-status-row">
          <span class="lmw-chip lmw-chip-scale ${this._isInScaleRange(layer) ? 'in-scale' : 'out-scale'}" data-scale-hint="${id}" title="${this._scaleHintText(layer)}">
            ${this._isInScaleRange(layer) ? '◉ In Scale' : '◌ Out of Scale'}
          </span>
          ${isEditMode && !canEditSymbol ? '<span class="lmw-chip lmw-chip-lock" title="Renderer is not SimpleRenderer. Symbol editing is disabled.">🔒 Server Style</span>' : ''}
        </div>
        <div class="lmw-sym-panel" data-sym-id="${id}" hidden>
          <div class="lmw-sym-inner">

            <!-- Opacity — all types -->
            ${this._sliderRow('opacity', id, 'Opacity', 0, 1, 0.01, sym.opacity, v => `${Math.round(v * 100)}%`)}

            <!-- Color — feature & map-image -->
            ${hasColor ? this._colorRow('color', id, g === 'polygon' ? 'Fill Color' : 'Color', sym.color) : ''}

            <!-- Outline color — points & polygons only -->
            ${isFeature && g && g !== 'polyline' ? this._colorRow('outlineColor', id, 'Outline Color', sym.outlineColor) : ''}

            <!-- Point controls -->
            ${isFeature && g === 'point' ? `
              ${this._sliderRow('size', id, 'Size', 4, 40, 1, sym.size, v => `${v}px`)}
              ${this._styleGridRow('style', id, 'Marker Style', MARKER_STYLES, sym.style, 'marker')}
            ` : ''}

            <!-- Line / polygon stroke width -->
            ${isFeature && g && g !== 'point' ? `
              ${this._sliderRow('width', id, g === 'polygon' ? 'Outline Width' : 'Width', 0.5, 10, 0.5, sym.width, v => `${v}px`)}
            ` : ''}

            <!-- Line style -->
            ${isFeature && g === 'polyline' ? `
              ${this._styleGridRow('style', id, 'Line Style', LINE_STYLES, sym.style, 'line')}
            ` : ''}

            <!-- Auto-detect pending notice -->
            ${isFeature && !g ? `
              <p class="lmw-notice">⏳ Loading layer geometry type…<br>
              Symbol controls will appear once detected.</p>
            ` : ''}

            ${isFeature && Array.isArray(cfg.fields) && cfg.fields.length && isEditMode ? this._attributeRow(id, cfg) : ''}

            <div class="lmw-btn-row">
              <button class="lmw-apply-btn" data-id="${id}" data-action="apply">Apply</button>
              <button class="lmw-reset-btn" data-id="${id}" data-action="reset">Reset</button>
            </div>

          </div>
        </div>
      </div>`;
  }

  // ── Control row builders ──────────────────────────────────────────────────

  _sliderRow (ctrl, id, label, min, max, step, value, fmtFn) {
    const display = fmtFn ? fmtFn(value) : String(value);
    return `
      <div class="lmw-ctrl-row">
        <label class="lmw-ctrl-label">${label}</label>
        <div class="lmw-ctrl-right">
          <input type="range" class="lmw-slider"
            min="${min}" max="${max}" step="${step}" value="${value}"
            data-id="${id}" data-ctrl="${ctrl}">
          <span class="lmw-val" data-disp="${id}__${ctrl}">${display}</span>
        </div>
      </div>`;
  }

  _colorRow (ctrl, id, label, value) {
    return `
      <div class="lmw-ctrl-row">
        <label class="lmw-ctrl-label">${label}</label>
        <div class="lmw-ctrl-right">
          <label class="lmw-color-wrap">
            <input type="color" class="lmw-color" value="${this._toHex(value)}"
              data-id="${id}" data-ctrl="${ctrl}">
            <span class="lmw-color-swatch" style="background:${this._toHex(value)}"></span>
          </label>
        </div>
      </div>`;
  }

  _styleGridRow (ctrl, id, label, styles, current, kind) {
    const buttons = styles.map(s => `
      <button class="lmw-style-btn ${s === current ? 'active' : ''}"
        data-id="${id}" data-ctrl="${ctrl}" data-val="${s}" title="${s}">
        ${kind === 'marker' ? this._markerSVG(s) : this._lineSVG(s)}
      </button>`).join('');
    return `
      <div class="lmw-ctrl-row lmw-ctrl-row--col">
        <label class="lmw-ctrl-label">${label}</label>
        <div class="lmw-style-grid lmw-style-grid--${kind}">${buttons}</div>
      </div>`;
  }

  _attributeRow (id, cfg) {
    const selected = new Set(cfg.visibleFields || cfg.fields.map(f => f.name));
    const html = cfg.fields
      .filter(f => f?.name)
      .map(f => `
        <label class="lmw-check" style="gap:6px;">
          <input type="checkbox" data-id="${id}" data-ctrl="attrField" data-val="${f.name}" ${selected.has(f.name) ? 'checked' : ''}>
          <span>${f.alias || f.name}</span>
        </label>
      `)
      .join('');
    return `
      <div class="lmw-ctrl-row lmw-ctrl-row--col">
        <label class="lmw-ctrl-label">Info Attributes</label>
        <div style="display:grid;gap:4px;max-height:120px;overflow:auto;">${html}</div>
      </div>
    `;
  }

  // ── Events ────────────────────────────────────────────────────────────────

  _bindEvents () {
    // Delegation: handle all events on the panel root
    this._panel.addEventListener('change', e => this._onChange(e));
    this._panel.addEventListener('input',  e => this._onInput(e));
    this._panel.addEventListener('click',  e => this._onClick(e));
  }

  _onChange (e) {
    const el = e.target;
    if (el.dataset.action === 'visibility') {
      this._states.get(el.dataset.id)?.layer && (this._states.get(el.dataset.id).layer.visible = el.checked);
      if (this._mode === 'edit') this._setDirty(true);
    }
    // Live-update inline color swatch preview when color input changes
    if (el.type === 'color' && el.dataset.ctrl) {
      const cs = el.closest('.lmw-color-wrap')?.querySelector('.lmw-color-swatch');
      if (cs) cs.style.background = el.value;
    }
  }

  _onInput (e) {
    const el = e.target;
    if (el.type !== 'range' || !el.dataset.ctrl) return;
    const { id, ctrl } = el.dataset;
    const disp = this._panel.querySelector(`[data-disp="${id}__${ctrl}"]`);
    if (!disp) return;
    const v = parseFloat(el.value);
    disp.textContent = ctrl === 'opacity' ? `${Math.round(v * 100)}%` : `${v}px`;
  }

  _onClick (e) {
    const el = e.target.closest('[data-action], [data-ctrl]');
    if (!el) {
      const row = e.target.closest('.lmw-row');
      if (row && this._mode === 'view') {
        const item = row.closest('.lmw-item');
        const id = item?.dataset?.id;
        if (id) this._zoomToLayer(id);
      }
      return;
    }
    const { action, ctrl, id, val } = el.dataset;

    switch (action) {
      case 'toggleEdit': this.setMode(this._mode === 'edit' ? 'view' : 'edit'); break;
      case 'discover' : if (typeof this._onRequestDiscover === 'function') this._onRequestDiscover(); break;
      case 'saveChanges': this._onSaveChanges(); break;
      case 'deleteLayer': this.removeLayer(id); if (this._mode === 'edit') this._setDirty(true); break;
      case 'toggleSym': this._toggleSym(id);   break;
      case 'apply'    : this._onApply(id); if (this._mode === 'edit') this._setDirty(true); break;
      case 'reset'    : this._onReset(id);      break;
      case 'collapse' : this._toggleCollapse(); break;
      case 'toggleAll': this._toggleAll();      break;
    }

    // Style picker button
    if (ctrl === 'style' && el.tagName === 'BUTTON') {
      const grid = el.closest('.lmw-style-grid');
      grid?.querySelectorAll('.lmw-style-btn').forEach(b => b.classList.toggle('active', b === el));
    }
  }

  _toggleSym (id) {
    const state = this._states.get(id);
    if (state?.config?.isSymbolEditable === false) return;
    const sym = this._panel.querySelector(`[data-sym-id="${id}"]`);
    if (!sym) return;
    sym.hidden = !sym.hidden;
    const btn = this._panel.querySelector(`.lmw-sym-toggle[data-id="${id}"]`);
    btn?.classList.toggle('active', !sym.hidden);
  }

  _onApply (id) {
    const state = this._states.get(id);
    if (!state) return;
    if (state.config?.isSymbolEditable === false) return;
    const { layer, sym, config: cfg } = state;

    // Collect current control values from DOM
    const symPanel = this._panel.querySelector(`[data-sym-id="${id}"]`);
    if (symPanel) {
      symPanel.querySelectorAll('[data-ctrl]').forEach(el => {
        const ctrl = el.dataset.ctrl;
        if (el.type === 'range')  sym[ctrl] = parseFloat(el.value);
        if (el.type === 'color')  sym[ctrl] = el.value;
        // style buttons: only the active one counts
        if (el.tagName === 'BUTTON' && ctrl === 'style' && el.classList.contains('active')) {
          sym[ctrl] = el.dataset.val;
        }
      });
      const selectedFields = [...symPanel.querySelectorAll('input[data-ctrl="attrField"]:checked')].map(x => x.dataset.val);
      if (Array.isArray(cfg.fields) && selectedFields.length) {
        cfg.visibleFields = selectedFields;
        cfg.popupTemplate = {
          title: cfg.displayField ? `{${cfg.displayField}}` : (cfg.title || cfg.id),
          content: [{
            type: 'fields',
            fieldInfos: cfg.fields
              .filter(f => selectedFields.includes(f.name))
              .map(f => ({ fieldName: f.name, label: f.alias || f.name, visible: true })),
          }],
        };
        if (layer && 'popupTemplate' in layer) {
          layer.popupTemplate = cfg.popupTemplate;
        }
      }
    }

    // Apply opacity to all layer types
    layer.opacity = sym.opacity;

    // Apply renderer for FeatureLayer
    if (cfg.type === 'feature' && cfg.geometryType) {
      this._applyRenderer(layer, sym, cfg.geometryType);
    }

    // Update main row swatch
    const swatch = this._panel.querySelector(`[data-swatch="${id}"]`);
    if (swatch) swatch.style.background = this._toHex(sym.color);

    // Flash the apply button
    const btn = this._panel.querySelector(`.lmw-apply-btn[data-id="${id}"]`);
    if (btn) { btn.textContent = '✓ Applied'; setTimeout(() => (btn.textContent = 'Apply'), 1200); }
  }

  _onReset (id) {
    const state = this._states.get(id);
    if (!state) return;

    // Restore default symbol from original config
    state.sym = this._defaultSym(state.config);
    const { layer, sym, config: cfg } = state;

    layer.opacity = sym.opacity;
    if (cfg.type === 'feature' && cfg.geometryType) {
      this._applyRenderer(layer, sym, cfg.geometryType);
    }

    // Re-render this item's HTML in place
    this._rebuildItem(id);
  }

  _toggleCollapse () {
    this._collapsed = !this._collapsed;
    this._panel.classList.toggle('lmw-collapsed', this._collapsed);
  }

  _onSaveChanges () {
    this._setBusy(true);
    if (typeof this._onRequestSave !== 'function') {
      this._setDirty(false);
      this._setBusy(false);
      return;
    }
    try {
      const result = this._onRequestSave(this.getRunningConfig());
      if (result && typeof result.then === 'function') {
        result.then(() => this._setDirty(false)).catch(() => {}).finally(() => this._setBusy(false));
      } else {
        this._setDirty(false);
        this._setBusy(false);
      }
    } catch {
      this._setBusy(false);
      // keep dirty state if save handler throws
    }
  }

  setBusy (busy) {
    this._setBusy(!!busy);
  }

  _setBusy (busy) {
    if (this._busy === busy) return;
    this._busy = busy;
    if (this._panel) this._panel.innerHTML = this._renderPanel();
  }

  _toggleAll () {
    const allOn = [...this._states.values()].every(s => s.layer.visible);
    this._states.forEach(({ layer }, id) => {
      layer.visible = !allOn;
      const cb = this._panel.querySelector(`input[data-id="${id}"][data-action="visibility"]`);
      if (cb) cb.checked = !allOn;
    });
  }

  async _zoomToLayer (id) {
    const state = this._states.get(id);
    if (!state) return;
    const { layer, config } = state;
    try {
      if (config.type === 'feature' && typeof layer.queryExtent === 'function') {
        const result = await layer.queryExtent({ where: '1=1' });
        const ext = result?.extent;
        if (ext) {
          await this._view.goTo(ext.expand?.(1.15) || ext).catch(() => {});
          return;
        }
      }
      const ext = layer.fullExtent;
      if (ext) {
        await this._view.goTo(ext.expand?.(1.1) || ext).catch(() => {});
      }
    } catch {
      // Keep interaction resilient; no-op if extent lookup fails.
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  _rebuildItem (id) {
    const state = this._states.get(id);
    if (!state) return;
    const existing = this._panel.querySelector(`.lmw-item[data-id="${id}"]`);
    if (!existing) return;
    const wasOpen = !this._panel.querySelector(`[data-sym-id="${id}"]`).hidden;
    existing.insertAdjacentHTML('afterend', this._renderItem(state));
    existing.remove();
    if (wasOpen) {
      const sym = this._panel.querySelector(`[data-sym-id="${id}"]`);
      if (sym) sym.hidden = false;
    }
  }

  _attachPanel () {
    let container = this._view.container;
    if (typeof container === 'string') container = document.getElementById(container);
    if (container) {
      container.style.position = 'relative';
      container.appendChild(this._panel);
    }
  }

  _bindScaleWatcher () {
    if (!this._view?.watch) return;
    this._scaleWatchHandle = this._view.watch('scale', () => this._refreshScaleHints());
  }

  _refreshScaleHints () {
    this._states.forEach(({ layer }, id) => {
      const el = this._panel?.querySelector(`[data-scale-hint="${id}"]`);
      if (!el) return;
      const inScale = this._isInScaleRange(layer);
      el.textContent = inScale ? '◉ In Scale' : '◌ Out of Scale';
      el.title = this._scaleHintText(layer);
      el.classList.toggle('in-scale', inScale);
      el.classList.toggle('out-scale', !inScale);
    });
  }

  _isInScaleRange (layer) {
    const s = this._view?.scale;
    if (!s) return true;
    const min = Number(layer?.minScale || 0);
    const max = Number(layer?.maxScale || 0);
    if (min > 0 && s > min) return false;
    if (max > 0 && s < max) return false;
    return true;
  }

  _scaleHintText (layer) {
    const s = Math.round(this._view?.scale || 0);
    const min = Number(layer?.minScale || 0);
    const max = Number(layer?.maxScale || 0);
    const fmt = (n) => `1:${Math.round(n).toLocaleString()}`;
    if (this._isInScaleRange(layer)) return `Visible at current scale (${fmt(s)})`;
    if (min > 0 && s > min) return `Zoom in to ${fmt(min)} or closer`;
    if (max > 0 && s < max) return `Zoom out to ${fmt(max)} or farther`;
    return `Not visible at current scale (${fmt(s)})`;
  }

  /** Convert any Esri-compatible color value to a CSS hex string. */
  _toHex (color) {
    if (!color) return DEFAULT_SYMBOL.color;
    if (typeof color === 'string') {
      if (/^#[0-9a-f]{3,6}$/i.test(color)) return color;
      // named / rgb() — use canvas trick
      try {
        const ctx = document.createElement('canvas').getContext('2d');
        ctx.fillStyle = color;
        return ctx.fillStyle;
      } catch { return DEFAULT_SYMBOL.color; }
    }
    if (Array.isArray(color)) {
      return '#' + color.slice(0, 3).map(v => Math.round(v).toString(16).padStart(2, '0')).join('');
    }
    if (color.r !== undefined) {
      return '#' + [color.r, color.g, color.b].map(v => Math.round(v).toString(16).padStart(2, '0')).join('');
    }
    return DEFAULT_SYMBOL.color;
  }

  _markerSVG (style) {
    const shapes = {
      circle  : '<circle cx="10" cy="10" r="7" fill="currentColor"/>',
      square  : '<rect x="3" y="3" width="14" height="14" fill="currentColor"/>',
      diamond : '<polygon points="10,2 18,10 10,18 2,10" fill="currentColor"/>',
      cross   : '<line x1="2" y1="10" x2="18" y2="10" stroke="currentColor" stroke-width="3"/>' +
                '<line x1="10" y1="2" x2="10" y2="18" stroke="currentColor" stroke-width="3"/>',
      x       : '<line x1="3" y1="3" x2="17" y2="17" stroke="currentColor" stroke-width="3"/>' +
                '<line x1="17" y1="3" x2="3" y2="17" stroke="currentColor" stroke-width="3"/>',
      triangle: '<polygon points="10,2 18,18 2,18" fill="currentColor"/>',
    };
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="16" height="16">${shapes[style] ?? ''}</svg>`;
  }

  _lineSVG (style) {
    const dash = {
      solid     : '',
      dash      : 'stroke-dasharray="8 4"',
      dot       : 'stroke-dasharray="2 4"',
      'dash-dot': 'stroke-dasharray="8 3 2 3"',
      'long-dash': 'stroke-dasharray="14 4"',
    };
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44 12" width="44" height="12">
      <line x1="2" y1="6" x2="42" y2="6" stroke="currentColor" stroke-width="2.5" ${dash[style] ?? ''}/>
    </svg>`;
  }

  _normalizeMarkerStyle (style) {
    if (!style) return null;
    return MARKER_STYLE_MAP[style] || null;
  }

  _normalizeLineStyle (style) {
    if (!style) return null;
    return LINE_STYLE_MAP[style] || null;
  }

  _normalizeLabelingInfo (labelingInfo) {
    if (!Array.isArray(labelingInfo) || !labelingInfo.length) return undefined;
    const normalized = labelingInfo
      .map((lc) => {
        if (!lc || typeof lc !== 'object') return null;
        const rawPlacement = lc.labelPlacement ? (LABEL_PLACEMENT_MAP[lc.labelPlacement] || lc.labelPlacement) : undefined;
        const symbol = this._normalizeLabelSymbol(lc.symbol);
        if (!symbol) return null;
        const out = {
          symbol,
          where: lc.where || undefined,
          minScale: lc.minScale ?? undefined,
          maxScale: lc.maxScale ?? undefined,
          deconflictionStrategy: lc.deconflictionStrategy || undefined,
          useCodedValues: lc.useCodedValues ?? undefined,
          labelExpression: lc.labelExpression || undefined,
          labelExpressionInfo: lc.labelExpressionInfo || undefined,
        };
        if (rawPlacement && VALID_LABEL_PLACEMENTS.has(rawPlacement)) {
          out.labelPlacement = rawPlacement;
        }
        return out;
      })
      .filter(Boolean);
    return normalized.length ? normalized : undefined;
  }

  _normalizeLabelSymbol (symbol) {
    if (!symbol || typeof symbol !== 'object') return null;
    const font = symbol.font && typeof symbol.font === 'object'
      ? {
          family: symbol.font.family || undefined,
          size: symbol.font.size ?? undefined,
          style: symbol.font.style || undefined,
          weight: symbol.font.weight || undefined,
        }
      : undefined;
    return {
      type: 'text',
      color: symbol.color || undefined,
      haloColor: symbol.haloColor || undefined,
      haloSize: symbol.haloSize ?? undefined,
      horizontalAlignment: symbol.horizontalAlignment || undefined,
      verticalAlignment: symbol.verticalAlignment || undefined,
      xoffset: symbol.xoffset ?? undefined,
      yoffset: symbol.yoffset ?? undefined,
      angle: symbol.angle ?? undefined,
      text: typeof symbol.text === 'string' ? symbol.text : undefined,
      font,
    };
  }

  _applySafeLabeling (layer, cfg) {
    try {
      const labelingInfo = this._normalizeLabelingInfo(cfg.labelingInfo);
      if (labelingInfo?.length) {
        layer.labelingInfo = labelingInfo;
        layer.labelsVisible = cfg.labelsVisible !== false;
      } else {
        layer.labelsVisible = false;
      }
    } catch {
      layer.labelsVisible = false;
    }
  }

  _hasBigIntegerField (cfg) {
    return Array.isArray(cfg?.fields) && cfg.fields.some(f =>
      (f?.type || '').toLowerCase().includes('biginteger'),
    );
  }

  _shouldForceMapImage (cfg) {
    if (cfg?.type !== 'feature') return false;
    if (!this._hasBigIntegerField(cfg)) return false;
    return /\/MapServer\/\d+\/?$/i.test(cfg.url || '');
  }

  _toMapImageFallbackConfig (cfg) {
    const m = (cfg.url || '').match(/^(.*\/MapServer)\/(\d+)\/?$/i);
    if (!m) return null;
    const rootUrl = m[1];
    const subId = Number(m[2]);
    return {
      id: cfg.id,
      title: cfg.title || cfg.id,
      type: 'map-image',
      url: rootUrl,
      visible: cfg.visible !== false,
      opacity: cfg.opacity ?? 1,
      token: cfg.token,
      sublayers: [{ id: subId, title: cfg.title || cfg.id, visible: true }],
    };
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Add a layer at runtime.
   * @param {LayerConfig} config
   */
  addLayer (config) {
    if (this._states.has(config.id)) {
      console.warn(`[LayerManagerWidget] Layer "${config.id}" already exists.`);
      return;
    }
    const layer = this._makeLayer(config);
    if (!layer) return;
    const sym = this._defaultSym(config);
    this._states.set(config.id, { config, layer, sym });
    const canApplySimpleRenderer = config.isSymbolEditable !== false && (!config.rendererType || config.rendererType === 'simple');
    if (config.type === 'feature' && config.geometryType && canApplySimpleRenderer) {
      this._applyRenderer(layer, sym, config.geometryType);
    }
    this._view.map.add(layer);
    const body = this._panel?.querySelector('.lmw-body');
    body?.insertAdjacentHTML('beforeend', this._renderItem({ config, layer, sym }));
  }

  /**
   * Remove a managed layer by id.
   * @param {string} id
   */
  removeLayer (id) {
    const state = this._states.get(id);
    if (!state) return;
    this._view.map.remove(state.layer);
    this._states.delete(id);
    this._panel?.querySelector(`.lmw-item[data-id="${id}"]`)?.remove();
  }

  /**
   * Get the underlying Esri layer object.
   * @param {string} id
   * @returns {__esri.Layer|undefined}
   */
  getLayer (id) {
    return this._states.get(id)?.layer;
  }

  setMode (mode) {
    const next = mode === 'edit' ? 'edit' : 'view';
    if (this._mode === next) return;
    this._mode = next;
    this._dirty = false;
    if (this._panel) this._panel.innerHTML = this._renderPanel();
    if (typeof this._onModeChange === 'function') this._onModeChange(this._mode);
  }

  _setDirty (dirty) {
    if (this._dirty === dirty) return;
    this._dirty = dirty;
    if (this._panel) this._panel.innerHTML = this._renderPanel();
    if (typeof this._onDirtyChange === 'function') this._onDirtyChange(this._dirty);
  }

  getRunningConfig () {
    return [...this._states.values()].map(({ config, layer, sym }) => ({
      ...config,
      title: config.title || config.id,
      visible: layer.visible,
      opacity: layer.opacity,
      symbol: config.type === 'feature' ? { ...(config.symbol || {}), ...sym } : config.symbol,
    }));
  }

  /**
   * Destroy the widget: removes all managed layers and the panel DOM.
   */
  destroy () {
    this._scaleWatchHandle?.remove?.();
    this._scaleWatchHandle = null;
    this._states.forEach(({ layer }) => this._view.map.remove(layer));
    this._states.clear();
    this._panel?.remove();
    this._panel = null;
  }
}
